import { PlusOutlined } from '@ant-design/icons';
import { Badge, Button, Checkbox, Popconfirm, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';

import { useAppDispatch, useAppSelector } from 'app/store';
import { useCancelMarkToSendingMutation, useMarkToSendingMutation } from 'app/services/edpApiV1/vacationsSchedule';
import { viewerModel } from 'entities/viewer';
import { MouseEventHandler, useEffect, useMemo, useState } from 'react';

import { RequestStatusType, PermissionAlias, RolesUser } from 'shared/enums';
import { getErrorData } from 'shared/helpers/utils';
import { useNotifications } from 'shared/hooks/use-notifications';
import SuperviserApproveMarker from 'shared/components/superviserApproveMarker';

import { DataScheduleType, TScheduleData } from 'shared/types/vacation-schedule.types';
import { getAllAvailableOrganizations } from 'entities/organizations/model/organizations';
import { addVacation, deleteVacation, getMultiSelectedVacations } from 'entities/vacations/model/multiSelect';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import style from './index.module.scss';

type TProps = {
  employeer: TScheduleData;
  showModal: (requestId: string | number, employeeId: string | number) => void;
  showModalAdd: (employeeId: string | number, name: string) => void;
  oldYearStatus: boolean;
  isUploadTo1S: boolean;
};

const Card = ({ employeer, showModal, showModalAdd, oldYearStatus, isUploadTo1S }: TProps) => {
  const [isVacationSсheldureDone, setIsVacationSсheldureDone] = useState<boolean | null>(null);
  const [isAgreeBtnDisabled, setIsAgreeBtnDisabled] = useState<boolean | null>(null);
  const [isCancelAgreeBtnDisabled, setIsCancelAgreeBtnDisabled] = useState<boolean | null>(null);
  const fullName = `${employeer?.lastName ?? ''} ${employeer?.name ?? ''} ${employeer?.middleName ?? ''}`;
  const allAvailableOrganizations = useAppSelector(getAllAvailableOrganizations);
  const dispatch = useAppDispatch();
  const employeerVacationShaldureStatusType = employeer?.scheduleRequests?.[0]?.status?.type;
  const multiVacations = useAppSelector(getMultiSelectedVacations);

  const [checked, setChecked] = useState<boolean>(false);

  useEffect(() => {
    const includes = multiVacations.data.includes(employeer.id);
    if (!checked && includes) {
      setChecked(true);
    }
    if (checked && !includes) {
      setChecked(false);
    }
  }, [multiVacations.data]);

  const isRejectBtnDisabled =
    oldYearStatus ||
    employeerVacationShaldureStatusType === RequestStatusType.rejectedByHR ||
    employeerVacationShaldureStatusType === RequestStatusType.approvedByHR ||
    isVacationSсheldureDone === true ||
    isUploadTo1S;

  const columns: ColumnsType<DataScheduleType> = [
    {
      title: '№',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: 'Вид отпуска',
      dataIndex: 'vacationType',
      key: 'vacationType',
      width: 500,
    },
    {
      title: 'Дата начала',
      dataIndex: 'startedAt',
      key: 'startedAt',
    },
    {
      title: 'Дата окончания',
      dataIndex: 'endedAt',
      key: 'endedAt',
    },
    {
      title: 'Дни',
      dataIndex: 'amountDays',
      key: 'amountDays',
    },
    {
      title: 'Действие',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record: { key: React.Key; status?: { type: RequestStatusType; name: string } | null }) => {
        return (
          <Popconfirm
            placement="leftTop"
            title="Вы уверены?"
            onConfirm={() => showModal(record.key, employeer?.id)}
            disabled={isRejectBtnDisabled}
          >
            <Button type="primary" danger disabled={isRejectBtnDisabled}>
              Отклонить
            </Button>
          </Popconfirm>
        );
      },
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: { type?: RequestStatusType; name: string; isSupervisorApproved: boolean }) => (
        <>
          <span className="d-flex">
            <Badge className={style.badge} status="processing" />
            {status.name}
          </span>
          <br />
          {!status.isSupervisorApproved && <SuperviserApproveMarker text="Не согласовано руководителем" />}
        </>
      ),
    },
  ];

  const { role, permissions } = useAppSelector(viewerModel.me.selectors.selectViewerProfile);

  const [cancelMarkToSendingTrigger, result] = useCancelMarkToSendingMutation({
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
  });
  const { isSuccess, isError, error, reset } = result;
  useNotifications(isSuccess, isError, 'Заявки отменены', error?.message, reset);

  const [martToSendingTrigger, resultMarkToSending] = useMarkToSendingMutation({
    selectFromResult: result => ({ ...result, error: getErrorData(result.error) }),
  });

  useEffect(() => {
    // true - блок кнопки
    // false - будет доступна для клика

    // проверка на статус на рассмотрении
    const isConsideredByHr = employeer?.scheduleRequests?.every(
      el => el.status?.type === RequestStatusType.consideredByHR,
    );
    // проверка на статус одобрено
    const isApprovedByHR = employeer?.scheduleRequests?.every(el => el.status?.type === RequestStatusType.approvedByHR);
    // проверка на доступность редактирования ГО
    const isVacantinDone = employeer?.scheduleRequests?.some(el => el.status?.type === RequestStatusType.ready);

    // следит за блокировкой кнопки согласовать
    if (isConsideredByHr === true && permissions?.includes(PermissionAlias.HRVacationScheduleEmployeeRequestsApprove)) {
      setIsAgreeBtnDisabled(false);
      setIsCancelAgreeBtnDisabled(true);
    } else {
      setIsAgreeBtnDisabled(true);
      setIsCancelAgreeBtnDisabled(true);
    }
    // следит за блокировкой кнопки отменить согласование
    if (
      isApprovedByHR === true &&
      permissions?.includes(PermissionAlias.HRVacationScheduleEmployeeRequestsCancelApprove)
    ) {
      setIsAgreeBtnDisabled(true);
      setIsCancelAgreeBtnDisabled(false);
    }
    // следит за блокировкой кнопки добавить
    if (isVacantinDone === true && permissions?.includes(PermissionAlias.HRVacationScheduleEmployeeRequestsCreate)) {
      setIsVacationSсheldureDone(true);
    } else {
      setIsVacationSсheldureDone(false);
    }
  }, [employeer]);

  const {
    isSuccess: successMarkToSending,
    isError: errorMarkToSending,
    error: errorMessage,
    reset: resetMarkToSending,
  } = resultMarkToSending;

  useNotifications(
    successMarkToSending,
    errorMarkToSending,
    'Заявка на отпуск согласована',
    errorMessage?.message,
    resetMarkToSending,
  );

  const onClickCheckbox = (event: CheckboxChangeEvent) => {
    if (event.target.checked) {
      dispatch(addVacation(employeer.id));
    } else {
      dispatch(deleteVacation(employeer.id));
    }
  };

  const isOnHRForScheduledRequest = useMemo(() => {
    return (
      (employeer?.scheduleRequests?.length || 0) > 0 &&
      employeer.scheduleRequests?.every(elem => elem?.status?.type === RequestStatusType.consideredByHR)
    );
  }, [employeer]);

  return (
    <div className={style.card}>
      <div className={style.cardTop}>
        <div className={style.employeer}>
          <div className={style.fio}>
            <Checkbox
              disabled={!isOnHRForScheduledRequest}
              checked={checked}
              onChange={(ev: CheckboxChangeEvent) => onClickCheckbox(ev)}
            />
            <h3 className={style.employeerFio}>{fullName}</h3>
          </div>
          <span className={style.employeerDivision}>{employeer?.division}</span>
          <span className={style.employeerPosition}>{employeer?.position}</span>
          <div className={style.personnelNumber}>Таб. №: {employeer?.personnelNumber}</div>
        </div>

        <div className={style.btnsGroup}>
          {(role === RolesUser.HRDirector || role === RolesUser.FirstLine || role === RolesUser.SecondLine) && (
            <Button
              type="default"
              disabled={
                oldYearStatus ||
                isCancelAgreeBtnDisabled === true ||
                isUploadTo1S ||
                !employeer?.scheduleRequests ||
                isVacationSсheldureDone === true
              }
              onClick={() => cancelMarkToSendingTrigger({ employeeId: employeer?.id })}
            >
              Отменить согласование
            </Button>
          )}
          <Button
            type="default"
            disabled={
              oldYearStatus ||
              isAgreeBtnDisabled === true ||
              isUploadTo1S ||
              !employeer?.scheduleRequests ||
              isVacationSсheldureDone === true
            }
            onClick={() => martToSendingTrigger({ employeeId: employeer?.id })}
          >
            Согласовать
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModalAdd(employeer?.id, fullName)}
            disabled={oldYearStatus || isVacationSсheldureDone === true || isUploadTo1S}
          >
            Добавить
          </Button>
        </div>
      </div>

      <div className={style.cardBody}>
        {employeer.scheduleRequests ? (
          <Table scroll={{ x: true }} columns={columns} dataSource={employeer.scheduleRequests} />
        ) : (
          <Table scroll={{ x: true }} columns={columns} dataSource={[]} />
        )}
      </div>
    </div>
  );
};

export default Card;
